/**
 * The deal background — the player's "full picture" before (and during) play.
 *
 * Lifted faithfully from the source brief (reference/Panic_At_The_Closing.docx): the cast and deal
 * specifics are written entirely in DEFINED-TERM style (the Bank, the Sponsor, the Sellers, the
 * Target, the existing lender, Swedish counsel) — there are no invented proper names, so none are
 * coined here. Rendered by components/Briefing.tsx, shown on the title screen (Brief.tsx) and again
 * via the in-game "Background" toggle (so a player can re-read the framing mid-deal without losing
 * their place). Lightly condensed for screen; nothing material to the scenarios is dropped.
 */

/** A short framing line shown under the headline before the full brief. */
export const DEAL_LEDE =
  "Welcome, Counsel. You are the lenders' lead counsel on a €180 million acquisition financing — your first major deal in that chair. Here is the full picture before you start.";

export interface BriefingSection {
  heading: string;
  /** One or more paragraphs of prose. */
  body: string[];
  /** Optional bulleted detail (used for the security package). */
  list?: string[];
}

export const DEAL_BACKGROUND: BriefingSection[] = [
  {
    heading: "The transaction",
    body: [
      "Your client is the Bank — the lead arranger, with one co-lender, of a syndicated facility financing the acquisition of a Finnish industrial group (the Target). The buyer, a mid-market private equity Sponsor, is acquiring 100% of the shares in the Target from its founding family (the Sellers) by way of a share purchase.",
      "The enterprise value is €240 million. The financing package is a €160 million senior secured term loan facility and a €20 million revolving credit facility. Part of the proceeds repays the Target's existing indebtedness to its existing lender — a foreign bank with its principal office in London — whose security over the Target's assets (business mortgage notes and share pledges) is to be released and transferred to the Bank against repayment on the closing date.",
    ],
  },
  {
    heading: "The security package",
    body: [
      "The package is governed primarily by Finnish law, with certain elements under Swedish law. The Target has two Swedish subsidiaries, which act as guarantors under the facilities agreement. It comprises:",
    ],
    list: [
      "a pledge over the shares in the Target, including blank endorsement of the physical share certificates;",
      "a Swedish-law pledge over the shares in the two Swedish subsidiaries;",
      "business mortgage notes over the Target's business assets — existing notes to be released by the existing lender and re-pledged to the Bank, plus new notes still to be applied for;",
      "real estate mortgage notes over the property on which the Target's manufacturing facility sits;",
      "a “soft pledge” over residential lease receivables — perfection by notification to tenants deferred until an event of default;",
      "a pledge over insurance receivables, assigned to the Bank.",
    ],
  },
  {
    heading: "Your role",
    body: [
      "You are the lenders' lead counsel — this is your first major deal in that role. Your job is to see the finance documents properly executed, every condition precedent satisfied, and both signing and closing achieved on time and without defects.",
      "Perfection of each security interest happens promptly after closing, in close coordination with local counsel — including the Bank's Swedish counsel, who is responsible for the Swedish-law security documentation and for the legal opinion confirming that the finance documents bind the Swedish subsidiaries.",
    ],
  },
  {
    heading: "The timeline",
    body: [
      "Signing is scheduled for Wednesday; closing follows on Friday — right before the Midsummer holiday weekend. The Bank's payment cut-off is 15:00 Helsinki time on the closing date.",
      "If funds are not released by then, closing will not happen: the Sponsor will not be happy, and the Sellers — who have a boat to catch — will be even less so.",
    ],
  },
];
